import React, { useState } from 'react';
import { Table, Button, Modal, Tag, Tooltip, Space } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@/shared/hooks/useNotification';
import { VehicleEnrichment } from '@/shared/components/VehicleEnrichment';
import { type VehicleFilterRequest, type EngineType } from '@/features/guides/api/generated';

const getEngineTypeColor = (engineType: EngineType): string => {
  const colors: Record<EngineType, string> = {
    'ICE': 'blue',
    'BEV': 'green',
    'PHEV': 'orange',
    'HEV': 'cyan',
    'REX': 'purple',
    'Unknown': 'default',
  };
  return colors[engineType] || 'default';
};

interface FiltersEditorProps {
  filters: VehicleFilterRequest[];
  onAddFilter: (filter: VehicleFilterRequest) => void;
  onRemoveFilter: (index: number) => void;
  onUpdateFilter: (index: number, filter: VehicleFilterRequest) => void;
}

export const FiltersEditor: React.FC<FiltersEditorProps> = ({
  filters,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
}) => {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
  const [isEditFilterOpen, setIsEditFilterOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newFilter, setNewFilter] = useState<VehicleFilterRequest>({});

  const handleAddFilter = () => {
    if (!newFilter.engineBaseType && !newFilter.manufacturer && !newFilter.modelSeries && !newFilter.euroCode && !newFilter.modelYearMin && !newFilter.modelYearMax) {
      notification.warning(t('guides.filterValidationTitle'), t('guides.filterValidationMessage'));
      return;
    }
    onAddFilter(newFilter);
    setNewFilter({});
    setIsAddFilterOpen(false);
  };

  const handleCancelAdd = () => {
    setIsAddFilterOpen(false);
    setNewFilter({});
  };

  const handleEditFilter = (index: number) => {
    setEditingIndex(index);
    setNewFilter(filters[index]);
    setIsEditFilterOpen(true);
  };

  const handleUpdateFilter = () => {
    if (!newFilter.engineBaseType && !newFilter.manufacturer && !newFilter.modelSeries && !newFilter.euroCode && !newFilter.modelYearMin && !newFilter.modelYearMax) {
      notification.warning(t('guides.filterValidationTitle'), t('guides.filterValidationMessage'));
      return;
    }
    if (editingIndex !== null) {
      onUpdateFilter(editingIndex, newFilter);
    }
    setNewFilter({});
    setIsEditFilterOpen(false);
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setIsEditFilterOpen(false);
    setNewFilter({});
    setEditingIndex(null);
  };

  return (
    <div style={{ width: '100%' }}>
      <Table
        dataSource={filters.map((f, idx) => ({ ...f, key: String(idx) }))}
        pagination={false}
        style={{ marginTop: 0 }}
        columns={[
          { title: t('guides.manufacturer'), dataIndex: 'manufacturer', key: 'manufacturer' },
          { title: t('guides.modelSeries'), dataIndex: 'modelSeries', key: 'modelSeries' },
          { 
            title: t('guides.engine'), 
            dataIndex: 'engineBaseType', 
            key: 'engineBaseType', 
            render: (engine: EngineType) => (
              <Tag color={getEngineTypeColor(engine)}>{engine}</Tag>
            ) 
          },
          { title: t('guides.eurocode'), dataIndex: 'euroCode', key: 'euroCode' },
          { title: t('guides.modelYearMin'), dataIndex: 'modelYearMin', key: 'modelYearMin' },
          { title: t('guides.modelYearMax'), dataIndex: 'modelYearMax', key: 'modelYearMax' },
          {
            title: t('guides.actions'),
            key: 'actions',
            render: (_: unknown, __: unknown, index: number) => (
              <Space>
                <Tooltip title={t('guides.editFilter')}>
                  <Button type="text" icon={<EditOutlined />} onClick={() => handleEditFilter(index)} />
                </Tooltip>
                <Tooltip title={t('guides.removeFilter')}>
                  <Button danger type="text" icon={<DeleteOutlined />} onClick={() => onRemoveFilter(index)} />
                </Tooltip>
              </Space>
            )
          }
        ]}
      />

      <div style={{ marginTop: 16 }}>
        <Button type="primary" onClick={() => setIsAddFilterOpen(true)}>
          {t('guides.addFilter')}
        </Button>
      </div>

      <Modal
        open={isAddFilterOpen}
        title={t('guides.addFilter')}
        onCancel={handleCancelAdd}
        onOk={handleAddFilter}
        width={800}
      >
        <VehicleEnrichment
          value={{
            manufacturer: newFilter.manufacturer ?? undefined,
            modelSeries: newFilter.modelSeries ?? undefined,
            engineBaseType: newFilter.engineBaseType as unknown as string,
            euroCode: newFilter.euroCode ?? undefined,
            modelYearMin: newFilter.modelYearMin ?? undefined,
            modelYearMax: newFilter.modelYearMax ?? undefined,
          }}
          onChange={(selection) => setNewFilter({
            manufacturer: selection.manufacturer,
            modelSeries: selection.modelSeries,
            engineBaseType: selection.engineBaseType as EngineType | undefined,
            euroCode: selection.euroCode,
            modelYearMin: selection.modelYearMin,
            modelYearMax: selection.modelYearMax,
          })}
          yearMode="range"
        />
      </Modal>

      <Modal
        open={isEditFilterOpen}
        title={t('guides.editFilter')}
        onCancel={handleCancelEdit}
        onOk={handleUpdateFilter}
        width={800}
      >
        <VehicleEnrichment
          value={{
            manufacturer: newFilter.manufacturer ?? undefined,
            modelSeries: newFilter.modelSeries ?? undefined,
            engineBaseType: newFilter.engineBaseType as unknown as string,
            euroCode: newFilter.euroCode ?? undefined,
            modelYearMin: newFilter.modelYearMin ?? undefined,
            modelYearMax: newFilter.modelYearMax ?? undefined,
          }}
          onChange={(selection) => setNewFilter({
            manufacturer: selection.manufacturer,
            modelSeries: selection.modelSeries,
            engineBaseType: selection.engineBaseType as EngineType | undefined,
            euroCode: selection.euroCode,
            modelYearMin: selection.modelYearMin,
            modelYearMax: selection.modelYearMax,
          })}
          yearMode="range"
        />
      </Modal>
    </div>
  );
};